import { ERRORS } from "./constants";

export const AUTH_ERROR_MESSAGES: Record<string, string> = {
  "auth/wrong-password": "Incorrect email or password.",
  "auth/user-not-found": "Incorrect email or password.",
  "auth/invalid-credential": "Incorrect email or password.",
  "auth/invalid-login-credentials": "Incorrect email or password.",
  "auth/invalid-email": "Please enter a valid email address.",
  "auth/user-disabled":
    "This account has been deactivated. Please contact your administrator.",
  "auth/too-many-requests":
    "Too many attempts. Please wait a few minutes and try again.",
  "auth/network-request-failed":
    "Network error. Check your connection and try again.",
};

export const GENERIC_FALLBACK = "Something went wrong. Please try again.";

export interface ResolvedError {
  message: string;
  known: boolean;
}

function errorToMessage(error: unknown): string {
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message;
  if (error && typeof error === "object" && "message" in error) {
    const m = (error as { message: unknown }).message;
    if (typeof m === "string") return m;
  }
  try {
    return String(error);
  } catch {
    // silent: inside the showError/error-resolution path — logging here risks recursion
    return GENERIC_FALLBACK;
  }
}

function extractAuthCode(error: unknown): string | null {
  if (error && typeof error === "object" && "code" in error) {
    const code = (error as { code: unknown }).code;
    if (typeof code === "string" && code.startsWith("auth/")) return code;
  }
  const match = errorToMessage(error).match(/auth\/[a-z-]+/);
  return match ? match[0] : null;
}

const ERROR_CODES = ERRORS as Record<string, string>;

export function resolveError(error: unknown): ResolvedError {
  if (typeof error === "string") {
    const mapped = ERROR_CODES[error];
    return mapped
      ? { message: mapped, known: true }
      : { message: error, known: true };
  }
  const code = extractAuthCode(error);
  if (code && AUTH_ERROR_MESSAGES[code]) {
    return { message: AUTH_ERROR_MESSAGES[code], known: true };
  }
  const raw = errorToMessage(error);
  const mapped = ERROR_CODES[raw];
  if (mapped) return { message: mapped, known: true };
  return { message: raw, known: false };
}

export function getLoginErrorMessage(error: unknown): string {
  const { message, known } = resolveError(error);
  return known ? message : GENERIC_FALLBACK;
}
